import { useCallback, useEffect, useRef, useState } from 'react';

export type SpeechLang =
  | 'en-AU'
  | 'yue-Hant-HK'
  | 'cmn-Hans-CN'
  | 'cmn-Hant-TW'
  | 'nl-NL'
  | 'en-IN'
  | 'en-GB'
  | 'en-US'
  | 'fr-FR'
  | 'de-DE'
  | 'id-ID'
  | 'it-IT'
  | 'ja-JP'
  | 'ko-KR'
  | 'pt-BR'
  | 'ru-RU'
  | 'es-ES';

export interface SpeechLangOption {
  label: string;
  value: SpeechLang;
}

/**
 * Supported speech recognition languages (Chrome/Edge Web Speech API).
 * en-AU is always first as the default. Remaining entries are sorted
 * alphabetically by label. BCP 47 codes follow the Chrome official demo
 * (e.g. cmn-Hans-CN, not zh-CN).
 * To add a language: append an entry and extend the SpeechLang union above.
 */
export const SPEECH_LANG_OPTIONS: SpeechLangOption[] = [
  { label: 'English (AU)', value: 'en-AU' },       // default — always first
  { label: 'Cantonese (HK)', value: 'yue-Hant-HK' },
  { label: 'Chinese (Simplified)', value: 'cmn-Hans-CN' },
  { label: 'Chinese (Traditional)', value: 'cmn-Hant-TW' },
  { label: 'Dutch', value: 'nl-NL' },
  { label: 'English (India)', value: 'en-IN' },
  { label: 'English (UK)', value: 'en-GB' },
  { label: 'English (US)', value: 'en-US' },
  { label: 'French', value: 'fr-FR' },
  { label: 'German', value: 'de-DE' },
  { label: 'Indonesian', value: 'id-ID' },
  { label: 'Italian', value: 'it-IT' },
  { label: 'Japanese', value: 'ja-JP' },
  { label: 'Korean', value: 'ko-KR' },
  { label: 'Portuguese (Brazil)', value: 'pt-BR' },
  { label: 'Russian', value: 'ru-RU' },
  { label: 'Spanish', value: 'es-ES' },
];

const LANG_STORAGE_KEY = 'voice_input_lang';

// ---------------------------------------------------------------------------
// Module-level shared language state
// All useSpeechInput instances subscribe here so changing the language in one
// field automatically syncs every other field on the page.
// ---------------------------------------------------------------------------
function _readStoredLang(): SpeechLang {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY) as SpeechLang | null;
    return stored ?? 'en-AU';
  } catch {
    return 'en-AU';
  }
}

let _sharedLang: SpeechLang = _readStoredLang();
const _langSubscribers = new Set<(lang: SpeechLang) => void>();

function _setSharedLang(newLang: SpeechLang): void {
  _sharedLang = newLang;
  try {
    localStorage.setItem(LANG_STORAGE_KEY, newLang);
  } catch {
    // Ignore — private browsing may block localStorage writes
  }
  _langSubscribers.forEach((fn) => fn(newLang));
}
// ---------------------------------------------------------------------------

type SpeechRecognitionConstructor = new () => SpeechRecognition;

function getSpeechRecognitionCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  // Non-null assertions are safe: `in` proves the property exists even though
  // the Window interface declares them optional (required for narrowing to work).
  if ('SpeechRecognition' in window) return window.SpeechRecognition!;
  if ('webkitSpeechRecognition' in window) return window.webkitSpeechRecognition!;
  return null;
}

export interface UseSpeechInputReturn {
  isSupported: boolean;
  isRecording: boolean;
  lang: SpeechLang;
  langOptions: readonly SpeechLangOption[];
  setLang: (lang: SpeechLang) => void;
  toggle: () => void;
}

/**
 * Manages Web Speech API lifecycle for a single textarea.
 *
 * - Language preference is persisted to localStorage under `voice_input_lang`.
 * - `onTranscript` is stored in a ref internally, so callers can safely pass a
 *   new function on every render without recreating the recognition handler.
 * - Aborts any in-flight recognition on unmount.
 *
 * Browser support: Chrome 25+, Edge 79+. Firefox and Safari desktop are not
 * supported. iOS Safari 14.5+ has partial support.
 */
export function useSpeechInput({
  onTranscript,
}: {
  onTranscript: (text: string) => void;
}): UseSpeechInputReturn {
  // Ref so the recognition handler always invokes the latest callback without
  // needing the recognition instance itself to be recreated.
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const SpeechRecognitionCtor = useRef(getSpeechRecognitionCtor()).current;
  const isSupported = SpeechRecognitionCtor !== null;

  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(false);

  const [lang, setLangState] = useState<SpeechLang>(() => _sharedLang);
  const langRef = useRef(lang);
  langRef.current = lang;

  // Subscribe to cross-instance language changes on mount, unsubscribe on unmount.
  useEffect(() => {
    _langSubscribers.add(setLangState);
    return () => {
      _langSubscribers.delete(setLangState);
    };
  }, []);

  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const setLang = useCallback((newLang: SpeechLang) => {
    _setSharedLang(newLang);
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    isRecordingRef.current = false;
    setIsRecording(false);
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognitionCtor || isRecordingRef.current) return;

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = langRef.current;
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[0]?.[0]?.transcript?.trim() ?? '';
      if (transcript) onTranscriptRef.current(transcript);
    };

    recognition.onerror = () => {
      recognitionRef.current = null;
      isRecordingRef.current = false;
      setIsRecording(false);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      isRecordingRef.current = false;
      setIsRecording(false);
    };

    recognitionRef.current = recognition;
    isRecordingRef.current = true;
    recognition.start();
    setIsRecording(true);
  }, [SpeechRecognitionCtor]);

  const toggle = useCallback(() => {
    if (isRecordingRef.current) stop();
    else start();
  }, [start, stop]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  return {
    isSupported,
    isRecording,
    lang,
    langOptions: SPEECH_LANG_OPTIONS,
    setLang,
    toggle,
  };
}
